import {AssetSnapshot, AssetType, HistoryMaterialStorage} from '@hconnect/common/types'
import {roundTo15MinIntervalStart} from '@hconnect/common/utils'
import {dataTestId} from '@hconnect/uikit'
import {CardSectionTitle} from '@hconnect/uikit/src/lib2'
import {Stack} from '@mui/material'
import moment, {Moment} from 'moment-timezone'
import {useTranslation} from 'react-i18next'

import {PlannerSwitch} from '../../../../../../shared/components/formComponents/PlannerSwitch'
import {useCurrentScheduleQuery} from '../../../../../../shared/hooks/api/schedules/useCurrentScheduleQuery'
import {useUpdateScheduleItem} from '../../../../../../shared/hooks/api/schedules/useUpdateScheduleItem'
import {useAssetHistoryDataByScheduleItemId} from '../../../../../../shared/hooks/api/useAssetHistoryDataByScheduleItemId'
import {useCurrentTimeRounded} from '../../../../../../shared/hooks/useCurrentTimeRounded'
import {usePlantConfig} from '../../../../../../shared/hooks/usePlantConfigData'
import {ScheduleItem} from '../../../../../../shared/interfaces/api'

import {useAttachedStorages} from './hooks'
import {RemainingStoragesSelect} from './RemainingStoragesSelect'
import {SelectStorages} from './SelectStorages'
import {SelectStoragesOrdered} from './SelectStoragesOrdered'

interface SelectStorageSectionProps {
  asset: AssetSnapshot
  scheduleItem: ScheduleItem
  startOfPlan: Moment
  endOfPlan: Moment
  availableStorages: HistoryMaterialStorage[]
  storagesById: Record<string, HistoryMaterialStorage>
}

export const SelectStorageSection: React.FC<SelectStorageSectionProps> = ({
  asset,
  scheduleItem,
  startOfPlan,
  endOfPlan,
  availableStorages,
  storagesById
}) => {
  const {t} = useTranslation()
  const {timezone_id: timezoneId} = usePlantConfig()
  const nowRounded = useCurrentTimeRounded({timezoneId, roundingFn: roundTo15MinIntervalStart})

  const isSiloNominationDisabled = moment.utc(scheduleItem.end).isBefore(nowRounded)

  const {data: schedule} = useCurrentScheduleQuery({range: [startOfPlan, endOfPlan]})
  const assetDataByScheduleItemId = useAssetHistoryDataByScheduleItemId({
    timeFrame: [startOfPlan, endOfPlan],
    schedule
  })

  const {updateScheduleItem} = useUpdateScheduleItem({
    startOfPlan,
    endOfPlan,
    assetDataByScheduleItemId
  })

  const {
    selectedSilos: {isOrderSpecified, ids: selectedSilosIds}
  } = scheduleItem

  const handleToggleSpecifyOrder = () => {
    updateScheduleItem({
      ...scheduleItem,
      selectedSilos: {...scheduleItem.selectedSilos, isOrderSpecified: !isOrderSpecified}
    })
  }

  const {
    selectedStorages,
    selectedStoragesOrdered,
    selectedAvailableStoragesIds,
    selectedRemainingStoragesIds,
    remainingStorages
  } = useAttachedStorages({
    storagesById,
    availableStorages,
    selectedSilosIds
  })

  const shouldRemainingStoragesSelectBeShown =
    !isSiloNominationDisabled && asset.type === AssetType.CementMill

  return (
    <Stack spacing={1.5} {...dataTestId('select_storage_section')}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <CardSectionTitle>{t('planning.produceTo')}</CardSectionTitle>
        <PlannerSwitch
          label={t('planning.specifyOrder')}
          value={isOrderSpecified}
          onChange={handleToggleSpecifyOrder}
          disabled={isSiloNominationDisabled}
          {...dataTestId('specify_order_switch')}
        />
      </Stack>
      {isOrderSpecified ? (
        <SelectStoragesOrdered
          selectedStorages={selectedStoragesOrdered}
          scheduleItem={scheduleItem}
          isReadOnly={isSiloNominationDisabled}
          startOfPlan={startOfPlan}
          endOfPlan={endOfPlan}
        />
      ) : (
        <SelectStorages
          selectedStorages={selectedStorages}
          scheduleItem={scheduleItem}
          startOfPlan={startOfPlan}
          endOfPlan={endOfPlan}
          isReadOnly={isSiloNominationDisabled}
        />
      )}
      {shouldRemainingStoragesSelectBeShown && (
        <RemainingStoragesSelect
          selectedAvailableStoragesIds={selectedAvailableStoragesIds}
          selectedRemainingStoragesIds={selectedRemainingStoragesIds}
          remainingStorages={remainingStorages}
          scheduleItem={scheduleItem}
          updateScheduleItem={updateScheduleItem}
        />
      )}
    </Stack>
  )
}
